import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import { LuChevronRight } from "react-icons/lu";
import { SidebarContext } from ".";
import { cn } from "../../../../helpers/utils/css-utils";
import MenuItem from "./menu-item";

/** The child whose href appears in the path; the first child when the group's own path is open. */
export function findActiveChild(items, pathname, groupActive) {
  const match = items.find(item => pathname.includes(item.href));
  return match || (groupActive ? items[0] : undefined);
}

/**
 * A rail entry with nested pages (e.g. Channels → Web Chat, Slack…). It opens
 * on its own pages; with the rail collapsed it acts as a link to its first page.
 */
export default function MenuItemGroup({ name, icon, tip, items, active, pathname }) {
  const { expanded } = useContext(SidebarContext);
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);

  const activeChild = findActiveChild(items, pathname, active);

  if (!expanded) {
    return <MenuItem icon={icon} name={name} tip={tip} href={items[0].href} active={active} />;
  }

  const renderedIcon = React.cloneElement(icon, {
    className: cn(icon.props.className, "w-[18px] h-[18px] transition-colors duration-150"),
  });

  return (
    <div>
      <button
        type="button"
        title={tip}
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        className={cn(
          `group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 outline-none transition-colors duration-150
           focus-visible:ring-2 focus-visible:ring-accent/40`,
          active
            ? "text-ink"
            : "text-ink/80 hover:bg-raised/60 hover:text-ink"
        )}
      >
        <span className={cn(
          "flex h-[18px] w-[18px] flex-none items-center justify-center",
          active ? "text-ink" : "text-muted group-hover:text-ink/80"
        )}>
          {renderedIcon}
        </span>
        <span className={cn("min-w-0 flex-1 truncate text-left text-[13.5px]", active ? "font-semibold" : "font-medium")}>{name}</span>
        <LuChevronRight aria-hidden="true" className={cn("h-4 w-4 flex-none text-muted transition-transform duration-200", { "rotate-90": open })} />
      </button>
      {open && (
        <div className="ml-[19px] mt-0.5 flex flex-col gap-0.5 border-l border-line pl-2">
          {items.map(item => (
            <MenuItem key={item.href} nested icon={item.icon} name={item.name} href={item.href}
              tip={item.tip} active={item === activeChild} />
          ))}
        </div>
      )}
    </div>
  );
}

MenuItemGroup.propTypes = {
  name: PropTypes.string,
  icon: PropTypes.element,
  tip: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, href: PropTypes.string, icon: PropTypes.element })).isRequired,
  active: PropTypes.bool,
  pathname: PropTypes.string,
};
